import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { supabase } from '../../lib/supabaseClient';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { LoadingSpinner } from '../../components/ui/LoadingState';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { usePreferences } from '../../hooks/usePreferences';
import { NotificationContainer } from '../../components/ui/NotificationToast';
import AddSoftwareModal from './components/AddSoftwareModal';
import EditSoftwareModal from './components/EditSoftwareModal';
import DeleteSoftwareConfirmationModal from './components/DeleteSoftwareConfirmationModal';

const SoftwareDashboard = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useSelector((state) => state.auth);
    const isAdmin = user?.role === 'admin' || user?.role === 'system_admin';
    const [isLoading, setIsLoading] = useState(true);
    const [licenses, setLicenses] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingSoftware, setEditingSoftware] = useState(null);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [softwareToDelete, setSoftwareToDelete] = useState(null);
    const { preferences } = usePreferences();
    const preferredFormat = preferences?.date_format || 'DD/MM/YYYY';

    useEffect(() => {
        fetchSoftwareLicenses();
        if (location.state?.message) {
            addNotification(location.state.message, location.state.type || 'info');
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location]);

    const fetchSoftwareLicenses = async () => {
        setIsLoading(true);
        try {
            // Fetch licenses and join with assignments count
            const { data, error } = await supabase
                .from('software_licenses')
                .select(`
                    *,
                    software_assignments(count)
                `)
                .order('name');

            if (error) throw error;

            const mappedLicenses = data.map(license => ({
                ...license,
                used_seats: license.software_assignments?.[0]?.count || 0
            }));

            setLicenses(mappedLicenses);
        } catch (error) {
            console.error('Error fetching software licenses:', error);
            addNotification(t('software_licenses.load_error', { message: error.message }), 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const addNotification = (message, type = 'info') => {
        setNotifications(prev => [...prev, { id: Date.now(), message, type }]);
    };

    const handleSoftwareAdded = (message, type) => {
        addNotification(message, type);
        fetchSoftwareLicenses();
    };

    const handleSoftwareUpdated = (message, type) => {
        addNotification(message, type);
        fetchSoftwareLicenses();
    };

    const handleSoftwareDeleted = () => {
        addNotification(t('software_licenses.deleted_successfully', 'Software license deleted successfully'), 'success');
        fetchSoftwareLicenses();
    };

    // KPI Calculations
    const totalAnnualSpend = licenses.reduce((sum, lic) => sum + (Number(lic.annual_cost) || 0), 0);
    
    const totalWastedSeatsCost = licenses.reduce((sum, lic) => {
        const unused = Math.max(0, lic.total_seats - lic.used_seats);
        const costPerSeat = lic.total_seats > 0 ? (lic.annual_cost / lic.total_seats) : 0;
        return sum + (unused * costPerSeat);
    }, 0);

    const upcomingRenewalsCount = licenses.filter(lic => {
        if (!lic.renewal_date) return false;
        const renewalDate = new Date(lic.renewal_date);
        const today = new Date();
        const sixtyDaysFromNow = new Date();
        sixtyDaysFromNow.setDate(today.getDate() + 60);
        return renewalDate >= today && renewalDate <= sixtyDaysFromNow;
    }).length;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <LoadingSpinner size="xl" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">{t('software_licenses.page_title')}</h1>
                    <p className="text-muted-foreground">{t('software_licenses.page_subtitle')}</p>
                </div>
                <Button iconName="Plus" onClick={() => setIsAddModalOpen(true)}>{t('software_licenses.add_new_software')}</Button>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-card p-6 rounded-xl border border-border shadow-sm flex items-center gap-4">
                    <div className="p-3 bg-primary/10 rounded-lg text-primary">
                        <Icon name="Cloud" size={24} />
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">{t('software_licenses.total_annual_spend')}</p>
                        <h2 className="text-2xl font-bold">{formatCurrency(totalAnnualSpend)}</h2>
                    </div>
                </div>
                <div className="bg-card p-6 rounded-xl border border-border shadow-sm flex items-center gap-4 text-error">
                    <div className="p-3 bg-error/10 rounded-lg">
                        <Icon name="TrendingDown" size={24} />
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">{t('software_licenses.total_wasted_seats')}</p>
                        <h2 className="text-2xl font-bold">{formatCurrency(totalWastedSeatsCost)}</h2>
                    </div>
                </div>
                <div className="bg-card p-6 rounded-xl border border-border shadow-sm flex items-center gap-4">
                    <div className="p-3 bg-warning/10 rounded-lg text-warning">
                        <Icon name="Calendar" size={24} />
                    </div>
                    <div>
                        <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider">{t('software_licenses.upcoming_renewals')}</p>
                        <h2 className="text-2xl font-bold">{upcomingRenewalsCount}</h2>
                    </div>
                </div>
            </div>

            {/* Software Table */}
            <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-border/60 dark:divide-border/35">
                        <thead className="bg-muted">
                            <tr>
                                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('software_licenses.software_name')}</th>
                                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('software_licenses.vendor')}</th>
                                <th scope="col" className="px-6 py-4 text-center text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('software_licenses.utilization')}</th>
                                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('software_licenses.compliance')}</th>
                                <th scope="col" className="px-6 py-4 text-right text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('software_licenses.annual_cost')}</th>
                                <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('software_licenses.renewal_date')}</th>
                                <th scope="col" className="px-6 py-4 text-center text-xs font-bold text-muted-foreground uppercase tracking-wider">{t('software_licenses.actions')}</th>
                            </tr>
                        </thead>
                        <tbody className="bg-card divide-y divide-border/50 dark:divide-border/30">
                            {licenses.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-muted-foreground/70">
                                        <Icon name="FileCode" size={48} className="mx-auto mb-4 opacity-20" />
                                        <p className="text-sm font-medium">{t('software_licenses.no_licenses_found')}</p>
                                        <Button 
                                            variant="outline" 
                                            className="mt-4"
                                            onClick={() => setIsAddModalOpen(true)}
                                        >
                                            {t('software_licenses.add_first_subscription')}
                                        </Button>
                                    </td>
                                </tr>
                            ) : (
                                licenses.map((lic) => (
                                    <tr 
                                        key={lic.id} 
                                        className="hover:bg-muted/80 dark:hover:bg-muted/50 transition-colors cursor-pointer group"
                                        onClick={() => navigate(`/software/${lic.id}`)}
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-bold text-foreground">{lic.name}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                            {lic.vendor}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex flex-col items-center">
                                                <span className="text-xs font-bold text-foreground/80">{lic.used_seats} / {lic.total_seats}</span>
                                                <div className="w-20 h-1.5 bg-muted/50 rounded-full mt-1.5 overflow-hidden">
                                                    <div 
                                                        className={`h-full ${lic.used_seats > lic.total_seats ? 'bg-red-500' : 'bg-primary'}`} 
                                                        style={{ width: `${Math.min(100, (lic.used_seats / lic.total_seats) * 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {lic.used_seats > lic.total_seats ? (
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-destructive/10 text-destructive border border-destructive/20">
                                                    {t('software_licenses.over_deployed')}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-success/10 text-success border border-success/20">
                                                    {t('software_licenses.compliant')}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-foreground">
                                            {formatCurrency(lic.annual_cost)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                                            {lic.renewal_date ? formatDate(lic.renewal_date, preferredFormat) : t('software_licenses.na')}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon" 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditingSoftware(lic);
                                                    }}
                                                    className="h-8 w-8 text-muted-foreground/70 hover:text-primary hover:bg-primary/10 dark:hover:bg-primary/20"
                                                    title={t('software_licenses.edit_software')}
                                                >
                                                    <Icon name="Pencil" size={16} />
                                                </Button>
                                                {isAdmin && (
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon" 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSoftwareToDelete(lic);
                                                            setShowDeleteConfirmModal(true);
                                                        }}
                                                        className="h-8 w-8 text-muted-foreground/70 hover:text-destructive hover:bg-destructive/10 dark:hover:bg-destructive/20"
                                                        title={t('software_licenses.delete_software', 'Delete Software')}
                                                    >
                                                        <Icon name="Trash2" size={16} />
                                                    </Button>
                                                )}
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon" 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/software/${lic.id}`);
                                                    }}
                                                    className="h-8 w-8 text-muted-foreground/70 hover:text-primary hover:bg-primary/10 dark:hover:bg-primary/20"
                                                    title={t('software_licenses.view_details')}
                                                >
                                                    <Icon name="ArrowRight" size={16} />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {isAddModalOpen && (
                <AddSoftwareModal 
                    onClose={() => setIsAddModalOpen(false)} 
                    onSoftwareAdded={handleSoftwareAdded}
                />
            )}

            {editingSoftware && (
                <EditSoftwareModal
                    software={editingSoftware}
                    onClose={() => setEditingSoftware(null)}
                    onSoftwareUpdated={handleSoftwareUpdated}
                />
            )}

            {showDeleteConfirmModal && softwareToDelete && (
                <DeleteSoftwareConfirmationModal
                    software={softwareToDelete}
                    onClose={() => {
                        setShowDeleteConfirmModal(false);
                        setSoftwareToDelete(null);
                    }}
                    onConfirmDelete={handleSoftwareDeleted}
                />
            )}

            <NotificationContainer 
                notifications={notifications} 
                onRemove={(id) => setNotifications(prev => prev.filter(n => n.id !== id))} 
            />
        </div>
    );
};

export default SoftwareDashboard;
